import React from 'react';
import { CloudUpload, Server, Code, Check, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { servicesData } from '../mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const iconMap = {
  CloudUpload: CloudUpload,
  Server: Server,
  Code: Code
};

const Services = () => {
  const { ref: servicesRef, isVisible } = useScrollAnimation();

  return (
    <section id="servicios" className="bg-slate-50 py-16 sm:py-20 lg:py-24">
      <div ref={servicesRef} className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 scroll-animate ${isVisible ? 'in-view' : ''}`}>
            {servicesData.sectionTitle}
          </h2>
          <p className={`text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed scroll-animate delay-200 ${isVisible ? 'in-view' : ''}`}>
            {servicesData.sectionSubtitle}
          </p>
        </div>

        {/* Service Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 sm:mb-16 max-w-5xl mx-auto">
          {servicesData.services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const delay = `delay-${(index + 1) * 200}`;
            return (
              <div
                key={service.id}
                className={`bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] group scroll-animate ${delay} ${isVisible ? 'in-view' : ''}`}
              >
                {/* Icon */}
                <div className="mb-6">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors duration-300">
                    <IconComponent className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-600" strokeWidth={2} />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-base text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>

                {/* Benefits List */}
                <ul className="space-y-3 mb-6 sm:mb-8">
                  {service.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                        <Check className="w-3 h-3 text-emerald-600" strokeWidth={3} />
                      </div>
                      <span className="text-sm sm:text-base text-gray-700">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA Link */}
                <button
                  onClick={service.cta.action}
                  className="group/cta inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold text-sm sm:text-base transition-all duration-300"
                >
                  <span className="relative">
                    {service.cta.text}
                    <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-emerald-600 group-hover/cta:w-full transition-all duration-300"></span>
                  </span>
                  <ArrowRight className="w-4 h-4 group-hover/cta:translate-x-1 transition-transform duration-300" strokeWidth={2.5} />
                </button>
              </div>
            );
          })}
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 sm:p-10 lg:p-12 text-center border border-blue-100">
          <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            {servicesData.finalCTA.text}
          </p>
          <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8">
            {servicesData.finalCTA.subtext}
          </p>
          <Button
            size="lg"
            onClick={servicesData.finalCTA.action}
            className="bg-slate-800 hover:bg-slate-900 text-white font-semibold px-8 py-6 text-base sm:text-lg rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            {servicesData.finalCTA.buttonText}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;
